import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useBranding } from "@/lib/branding";
import { useAuth } from "@/lib/auth";
import PayPalModal from "@/components/PayPalModal";
import CashAppModal from "@/components/CashAppModal";
import {
  ArrowRight,
  Award,
  BookOpen,
  Briefcase,
  Building2,
  Calculator,
  Check,
  ClipboardCheck,
  Sparkles,
  Wallet,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const PLAN_1120 = { id: "corporate", name: "Mastering Form 1120", amount: 250 };

export default function Refresher1120() {
  const { branding } = useBranding();
  const { user } = useAuth();
  const [paypalOpen, setPaypalOpen] = useState(false);
  const [cashOpen, setCashOpen] = useState(false);
  const [paypalHandle, setPaypalHandle] = useState("Dynamictax80");
  const [cashTag, setCashTag] = useState("$DimekaH");

  useEffect(() => {
    axios.get(`${API}/api/payments/plans`).then((r) => {
      setPaypalHandle(r.data?.paypal_handle || "Dynamictax80");
      setCashTag(r.data?.cash_app_tag || "$DimekaH");
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="refresher-1120-page">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">
                {branding.brand_name?.[0]}
              </div>
            )}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">Home</Link>
            <Link to="/pricing" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">Pricing</Link>
            <Link to="/afsp-bundle" className="hidden sm:inline underline text-accent hover:text-accent/80 font-medium">AFSP Prep</Link>
            <Link to="/white-label" className="hidden sm:inline underline text-primary hover:text-primary/80 font-medium">White-Label</Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(214,138,61,0.28) 0%, rgba(15,23,42,0.98) 55%, #0a0f1f 100%)",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-20 lg:py-24 text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-xs font-medium">
            <Building2 size={13} className="text-accent" /> For working tax preparers
          </div>
          <h1
            className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] text-white mb-6"
            data-testid="refresher-hero-title"
          >
            Master Form 1120.
            <br />
            <span className="italic bg-clip-text text-transparent bg-gradient-to-r from-amber-200 via-white to-amber-200">
              Add C-Corps to your book.
            </span>
          </h1>
          <p className="text-lg md:text-xl text-white/70 mb-3 tracking-wide">
            The 13-chapter refresher built for preparers who already file 1040s.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-4 text-xs text-white/80">
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/20">13 chapters</span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/20">13 practice returns</span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/20">20-question final exam</span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/20">Downloadable certificate</span>
          </div>

          <div className="mt-12 inline-flex items-baseline gap-3">
            <span className="font-serif text-6xl md:text-7xl text-white">$250</span>
            <span className="text-sm text-white/60">/ one-time</span>
          </div>
          <div className="mt-2 text-xs text-accent font-medium tracking-wide">
            LIFETIME ACCESS · AI TEACHER INCLUDED
          </div>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Button
              size="lg"
              className="bg-[#0070ba] hover:bg-[#005ea6] text-white"
              onClick={() => setPaypalOpen(true)}
              data-testid="refresher-cta-paypal"
            >
              <Wallet size={16} className="mr-2" /> Pay $250 with PayPal
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/40 text-white hover:bg-white/10"
              onClick={() => setCashOpen(true)}
              data-testid="refresher-cta-cashapp"
            >
              <Wallet size={16} className="mr-2" /> Pay with Cash App
            </Button>
            <Button asChild size="lg" variant="ghost" className="text-white hover:bg-white/10" data-testid="refresher-cta-preview">
              <Link to="/course/1120">Preview Chapter 1 free</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="border-t border-border bg-secondary/40">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">Built for preparers, not students</div>
            <h2 className="font-serif text-4xl md:text-5xl">
              This is for you if you're…
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {AUDIENCE.map((a, i) => (
              <div
                key={i}
                className="border border-border rounded-lg p-6 bg-card"
                data-testid={`refresher-audience-${i}`}
              >
                <div className="h-10 w-10 rounded-md bg-primary/10 text-primary grid place-items-center mb-3">
                  {a.icon}
                </div>
                <h3 className="font-serif text-lg mb-2">{a.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section className="border-t border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center mb-12">
            <div className="label-caps mb-3 text-primary">The 13 chapters</div>
            <h2 className="font-serif text-4xl md:text-5xl">
              Every line, every schedule, <span className="italic text-primary">line by line.</span>
            </h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
              You'll walk out able to prepare Form 1120 (with L, M-1, M-2, M-3) for real corporate clients — from single-owner professional corps to mid-market C-Corps with inventory and depreciation.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {CHAPTERS.map((title, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`refresher-ch-${i + 1}`}>
                <div className="shrink-0 h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-serif grid place-items-center mt-0.5">
                  {i + 1}
                </div>
                <div className="text-sm leading-relaxed pt-0.5">{title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="border-t border-border bg-secondary/30">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">What $250 gets you</div>
            <h2 className="font-serif text-4xl md:text-5xl">Ready to bill C-Corp fees.</h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {INCLUDED.map((item, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`refresher-inc-${i}`}>
                <div className="mt-0.5 h-5 w-5 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                  <Check size={13} />
                </div>
                <span className="text-sm leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">Frequently asked</div>
            <h2 className="font-serif text-4xl md:text-5xl">
              The short version.
            </h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="refresher-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`refresher-faq-item-${i}`}>
                <AccordionTrigger className="text-left font-serif text-lg">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-24 text-center">
          <h2 className="font-serif text-4xl md:text-6xl mb-4">
            Bill smarter this <span className="italic text-primary">corporate season.</span>
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
            The average preparer bills <strong className="text-foreground">$800–$1,500</strong> per Form 1120. Get one client and this course pays for itself six times over.
          </p>
          <div className="inline-flex items-baseline gap-3 mb-8">
            <span className="font-serif text-5xl">$250</span>
            <span className="text-sm text-muted-foreground">· one-time · lifetime access</span>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button size="lg" className="bg-[#0070ba] hover:bg-[#005ea6] text-white" onClick={() => setPaypalOpen(true)} data-testid="refresher-final-cta-paypal">
              <Wallet size={16} className="mr-2" /> Pay $250 with PayPal
            </Button>
            <Button size="lg" variant="outline" onClick={() => setCashOpen(true)} data-testid="refresher-final-cta-cashapp">
              <Wallet size={16} className="mr-2" /> Pay with Cash App
            </Button>
            <Button asChild size="lg" variant="ghost" data-testid="refresher-final-cta-preview">
              <Link to="/course/1120">Preview Chapter 1 free</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-3">
          <div>Powered by <span className="text-foreground">{branding.brand_name}</span></div>
          <div className="flex gap-4">
            <Link to="/" className="underline hover:text-foreground">Home</Link>
            <Link to="/pricing" className="underline hover:text-foreground">Pricing</Link>
            <Link to="/white-label" className="underline hover:text-foreground">White-Label</Link>
          </div>
        </div>
      </footer>

      <PayPalModal
        open={paypalOpen}
        onClose={() => setPaypalOpen(false)}
        plan={PLAN_1120}
        paypalHandle={paypalHandle}
        user={user}
      />
      <CashAppModal
        open={cashOpen}
        onClose={() => setCashOpen(false)}
        plan={PLAN_1120}
        cashTag={cashTag}
        user={user}
      />
    </div>
  );
}

const AUDIENCE = [
  {
    icon: <ClipboardCheck size={22} />,
    title: "Filing 1040s already",
    body: "You've got the individual side down, but corporate clients keep asking and you're turning them away or referring them out.",
  },
  {
    icon: <Briefcase size={22} />,
    title: "Booking your first C-Corp client",
    body: "You landed one and want to prepare their 1120 confidently — no guessing at Schedule L, no fumbling with M-1 reconciliations.",
  },
  {
    icon: <BookOpen size={22} />,
    title: "Refreshing before season",
    body: "You've done 1120s before but need a fast, current refresher on the schedules, deductions, and 2025 limits before January hits.",
  },
];

const CHAPTERS = [
  "The C-Corporation Foundation & When to File Form 1120",
  "Form 1120 Page 1: The Income Section, Line by Line",
  "Cost of Goods Sold — Form 1125-A & Inventory",
  "Deductions Part 1 — Compensation, Salaries, and Employee Costs",
  "Deductions Part 2 — Depreciation, §179, Bonus, and Form 4562",
  "Deductions Part 3 — Advertising, Meals, Interest, and 'Other Deductions'",
  "Schedule C — Dividends & the Dividends-Received Deduction (DRD)",
  "Schedule J — Tax Computation, Credits, and Estimated Tax",
  "Schedule K — Other Information the IRS Wants to Know",
  "Schedule L — Balance Sheets per Books",
  "Schedule M-1 & M-3 — Reconciling Book Income to Tax Income",
  "Schedule M-2 — Analysis of Retained Earnings",
  "Special Situations & Common Preparer Traps",
];

const INCLUDED = [
  "13 in-depth video-style chapters written for working preparers",
  "AI tax teacher trained on the full course — ask any 1120 question 24/7",
  "13 fully guided practice returns with realistic corporate clients",
  "Interactive Schedule L balance-sheet builder",
  "M-1 book-to-tax reconciliation walkthrough",
  "Depreciation lookup tables + §179 phaseout calculator",
  "20-question final exam (80% to pass)",
  "Downloadable, dated certificate to send clients",
  "Lifetime access — every future 2026/2027 update included",
  "Free updates whenever the IRS publishes new limits",
];

const FAQS = [
  {
    q: "How is this different from the 1040 course?",
    a: "The 1040 course teaches you to prepare individual returns from scratch. This is a refresher/expansion for preparers who already do 1040s — assumes you know AGI, taxable income, and Form W-2, and jumps straight into corporate mechanics (Schedule L, M-1, M-2, DRD, §179 for corps, etc.).",
  },
  {
    q: "Do I need to have done a corporate return before?",
    a: "No. But you should be comfortable with 1040-level tax mechanics. If you're brand new to tax prep, start with Master the 1040 first — you'll come back to this one fluent in the basics.",
  },
  {
    q: "Are the 2025 limits included?",
    a: "Yes. §179 cap ($1,250,000), bonus depreciation (40%), corporate rate (flat 21%), and every other 2025 limit is embedded throughout the course. When the IRS publishes 2026 numbers, we update the course and you get the refresh at no charge.",
  },
  {
    q: "Can I bill clients for 1120 returns after finishing this?",
    a: "That's the whole point. Every chapter is written from a practitioner's POV — what you need to ask the client, what to enter on which line, what triggers IRS scrutiny, what to bill for. The 13 practice returns are modeled on real client scenarios.",
  },
  {
    q: "Do you offer a certificate?",
    a: "Yes — pass the 20-question final exam with 80% or higher and you get a downloadable PDF certificate dated the day you complete it. Perfect for your website, LinkedIn, or client-facing marketing.",
  },
  {
    q: "What's the refund policy?",
    a: "Because course content unlocks the moment you pay, all sales are final once you begin Chapter 1. Before opening any chapter, you can request a 100% refund within 7 days — just email support and we'll process it.",
  },
];
